import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga4';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBars, 
  faCaretDown, 
  faRss, 
  faSignOutAlt 
} from '@fortawesome/free-solid-svg-icons';
import defaultAvatar from '../../img_attributions/profile-icon-png-896.png';

const Navigation = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);

  const logout = useCallback(() => oktaAuth.signOut(), [oktaAuth]);

  const toggleMobileMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search });
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Handle outside click and Escape key to close mobile dropdown
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  if (!authState) return null;

  const authNav = authState.isAuthenticated ? (
    <>
      <li className="nav-item">
        <NavLink className="nav-link font-weight-bold" to="/profile">
          {t('nav.profile', 'Profile')}
        </NavLink>
      </li>
      <li className="nav-item">
        <NavLink className="nav-link d-flex align-items-center" to="/feed/feedhome">
          <FontAwesomeIcon icon={faRss} className="me-1 text-warning" />
          {t('nav.personalizedRss', 'Personalized RSS')}
        </NavLink>
      </li>
      <li className="nav-item">
        <button className="btn btn-link nav-link text-warning d-flex align-items-center" onClick={logout}>
          <FontAwesomeIcon icon={faSignOutAlt} className="me-1" />
          {t('nav.logout', 'Logout')}
        </button>
      </li>
    </>
  ) : (
    <>
      <li className="nav-item">
        <NavLink className="nav-link" to="/login">
          {t('nav.login', 'Login')}
        </NavLink>
      </li>
      <li className="nav-item">
        <NavLink className="nav-link text-warning font-weight-bold" to="/register">
          {t('nav.register', 'Register')}
        </NavLink>
      </li>
      <li className="nav-item nav-separator d-flex align-items-center text-white-50 px-2" aria-hidden="true">
        <span className="nav-divider">|</span>
      </li>
      <li className="nav-item">
        <NavLink className="nav-link d-flex align-items-center" to="/feed/feedhome">
          <FontAwesomeIcon icon={faRss} className="me-1 text-warning" />
          {t('nav.rssFeed', 'RSS Feed')}
        </NavLink>
      </li>
    </>
  );

  return (
    <nav className="navbar navbar-expand-lg navbar-dark bg-dark fixed-top shadow-sm">
      <div className="container-fluid">
        <Link to="/" className="navbar-brand text-warning font-weight-bold d-flex align-items-center">
          <img 
            src={(process.env.PUBLIC_URL || '') + "/favicon-32x32.png?v=0.1.31"} 
            alt={authState?.isAuthenticated ? "PCD Home - is an App, that enables to view and synchronize fitness data from and to 3rd party services like Huawei Health, Samsung Health and others the sleep, activity (like walking) and overall readiness (like relaxation metrics) and uses Google or OKTA Sign-In to authenticate users and enable secure access of the data." : "CD Home - is an Website App, that enables to view and synchronize fitness data from and to 3rd party services like Huawei Health, Samsung Health and others the sleep, activity (like walking) and overall readiness (like relaxation metrics) and uses Google or OKTA Sign-In to authenticate users and enable secure access of the data."} 
            className="me-2" 
            style={{ width: '24px', height: '24px', marginTop: '-4px' }} 
          />
          {authState?.isAuthenticated ? <><abbr title="Personalized Cloud Dashboard">PCD</abbr> Home</> : <><abbr title="Cloud Dashboard">CD</abbr> Home</>}
        </Link>

        {/* Mobile Dropdown for smaller screens / phones: styled identically to main page routes Menu pill */}
        <div className="d-lg-none ms-auto position-relative" ref={mobileMenuRef}>
          <button
            type="button"
            className="nav-link text-white bg-dark rounded-pill px-3 py-1 font-weight-bold shadow-sm d-flex align-items-center border border-secondary"
            style={{ fontSize: "0.85rem", cursor: "pointer" }}
            onClick={toggleMobileMenu}
            aria-expanded={isMenuOpen}
            aria-haspopup="true"
            aria-label={t('nav.menu', 'Menu')}
            data-testid="mobile-menu-toggle"
          >
            <FontAwesomeIcon icon={faBars} className="me-1" />
            <span>{t('nav.menu', 'Menu')}</span>
            <FontAwesomeIcon icon={faCaretDown} className="ms-1" style={{ fontSize: "0.75rem" }} />
          </button>

          {isMenuOpen && (
            <div
              className="mobile-nav-menu shadow-lg"
              role="menu"
              data-testid="mobile-nav-menu"
            >
              {authState.isAuthenticated ? (
                <>
                  <NavLink
                    className="mobile-nav-item text-decoration-none"
                    to="/profile"
                    onClick={closeMobileMenu}
                    role="menuitem"
                  >
                    <img 
                      src={defaultAvatar} 
                      alt="" 
                      className="rounded-circle me-2 border border-secondary" 
                      style={{ width: '18px', height: '18px', objectFit: 'cover' }} 
                    />
                    {t('nav.profile', 'Profile')}
                  </NavLink>
                  <NavLink
                    className="mobile-nav-item text-decoration-none"
                    to="/feed/feedhome"
                    onClick={closeMobileMenu}
                    role="menuitem"
                  >
                    <FontAwesomeIcon icon={faRss} className="me-2 text-warning" />
                    {t('nav.personalizedRss', 'Personalized RSS')}
                  </NavLink>
                  <div className="mobile-nav-divider" />
                  <button
                    type="button"
                    className="mobile-nav-item text-start border-0 bg-transparent text-danger fw-bold w-100 d-flex align-items-center"
                    onClick={() => {
                      closeMobileMenu();
                      logout();
                    }}
                    role="menuitem"
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                    {t('nav.logout', 'Logout')}
                  </button>
                </>
              ) : (
                <>
                  <NavLink
                    className="mobile-nav-item text-decoration-none text-primary fw-bold"
                    to="/login"
                    onClick={closeMobileMenu}
                    role="menuitem"
                  >
                    {t('nav.login', 'Login')}
                  </NavLink>
                  <NavLink
                    className="mobile-nav-item text-decoration-none text-warning font-weight-bold"
                    to="/register"
                    onClick={closeMobileMenu}
                    role="menuitem"
                  >
                    {t('nav.register', 'Register')}
                  </NavLink>
                  <div className="mobile-nav-divider" />
                  <NavLink
                    className="mobile-nav-item text-decoration-none"
                    to="/feed/feedhome"
                    onClick={closeMobileMenu}
                    role="menuitem"
                  >
                    <FontAwesomeIcon icon={faRss} className="me-2 text-warning" />
                    {t('nav.rssFeed', 'RSS Feed')}
                  </NavLink>
                </>
              )}
            </div>
          )}
        </div>

        {/* Desktop Navbar for screens that fit all header elements */}
        <div className="collapse navbar-collapse d-none d-lg-flex" id="pcdNavbarNav">
          <ul className="navbar-nav ms-auto align-items-center">
            {authNav}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;